<template>
  <div class="search-page">
    <section class="hero" aria-labelledby="search-hero-title">
      <div class="hero-background" aria-hidden="true" />
      <div class="hero-content">
        <h1 id="search-hero-title" class="hero-title">
          Review your <span class="highlight-word">past</span> courses and find your <span class="highlight-word">next</span> one.
        </h1>
        <p class="hero-subtitle">Search and filter CE courses by competency, cost, credits, and more.</p>
      </div>
    </section>

    <div class="search-container">
      <!-- Search Filters Sidebar -->
      <aside class="filters-sidebar" aria-label="Search filters">
        <div class="filters-header">
          <h2 class="filters-title">Filters</h2>
          <button
            v-if="hasActiveFilters"
            type="button"
            class="clear-filters-btn"
            @click="clearFilters"
          >
            Clear All
          </button>
        </div>

        <form @submit.prevent="handleSearch" class="filters-form">
          <!-- Text Search -->
          <div class="filter-group">
            <label for="query">Search</label>
            <input
              type="text"
              id="query"
              v-model="filters.query"
              placeholder="Search by name, description, provider..."
              class="filter-input"
              @input="debouncedSearch"
            />
          </div>

          <!-- Competency -->
          <div class="filter-group">
            <label for="competencyId">Competency</label>
            <select id="competencyId" v-model="filters.competencyId" class="filter-input" @change="handleSearch">
              <option value="">All Competencies</option>
              <option v-for="comp in filterOptions.competencies" :key="comp.id" :value="comp.id">
                {{ comp.name }}
              </option>
            </select>
          </div>

          <!-- Course / Activity Type -->
          <div class="filter-group">
            <label for="courseType">Course / Activity Type</label>
            <select id="courseType" v-model="filters.courseType" class="filter-input" @change="handleSearch">
              <option value="">All Types</option>
              <option v-for="type in courseTypeOptions" :key="type" :value="type">
                {{ type }}
              </option>
            </select>
          </div>

          <!-- Cost Type -->
          <div class="filter-group">
            <label for="costType">Cost</label>
            <select id="costType" v-model="filters.costType" class="filter-input" @change="handleSearch">
              <option value="">All</option>
              <option value="free">Free</option>
              <option value="paid">Paid</option>
            </select>
          </div>

          <!-- Max Cost (only show if paid is selected) -->
          <div v-if="filters.costType === 'paid'" class="filter-group">
            <label for="maxCost">Maximum Cost ($)</label>
            <input
              type="number"
              id="maxCost"
              v-model.number="filters.maxCost"
              :max="filterOptions.maxCost"
              min="0"
              step="0.01"
              placeholder="0.00"
              class="filter-input"
              @input="debouncedSearch"
            />
          </div>

          <!-- Credits Range -->
          <div class="filter-group">
            <label>CE Credits</label>
            <div class="range-inputs">
              <input
                type="number"
                v-model.number="filters.minCredits"
                :min="filterOptions.creditsRange.min"
                :max="filterOptions.creditsRange.max"
                placeholder="Min"
                class="filter-input range-input"
                @input="debouncedSearch"
              />
              <span class="range-separator">to</span>
              <input
                type="number"
                v-model.number="filters.maxCredits"
                :min="filterOptions.creditsRange.min"
                :max="filterOptions.creditsRange.max"
                placeholder="Max"
                class="filter-input range-input"
                @input="debouncedSearch"
              />
            </div>
          </div>
        </form>
      </aside>

      <!-- Search Results -->
      <main class="results-section" :aria-busy="loading">
        <div v-if="loading" class="loading-state">
          <span class="spinner" aria-hidden="true" />
          <p>Searching courses…</p>
        </div>

        <div v-else-if="error" class="error-state">
          <p>{{ error }}</p>
        </div>

        <div v-else>
          <!-- Results Header -->
          <div class="results-header">
            <div class="results-count">
              <strong>{{ searchResults.pagination.totalResults }}</strong> course{{ searchResults.pagination.totalResults !== 1 ? 's' : '' }} found
            </div>
            <div class="results-sort">
              <label for="sortBy">Sort by:</label>
              <select id="sortBy" v-model="filters.sortBy" class="results-header-select" @change="handleSearch">
                <option value="createdAt">Date Added</option>
                <option value="name">Name</option>
                <option value="credits">Credits</option>
                <option value="cost">Cost</option>
                <option value="reviewCount">Number of Reviews</option>
                <option value="averageOverallValue">Overall Rating</option>
                <option value="averageEducationalContent">Educational Content Rating</option>
                <option value="averageEngagement">Engagement Rating</option>
              </select>
              <button
                type="button"
                class="sort-direction-btn"
                :title="filters.sortOrder === 'asc' ? 'Ascending (click for descending)' : 'Descending (click for ascending)'"
                :aria-label="filters.sortOrder === 'asc' ? 'Sort ascending, click to sort descending' : 'Sort descending, click to sort ascending'"
                @click="toggleSortOrder"
              >
                {{ filters.sortOrder === 'asc' ? '↑' : '↓' }}
              </button>
            </div>
            <div class="results-per-page">
              <label for="pageSize">Results per page:</label>
              <select id="pageSize" v-model.number="filters.pageSize" @change="handleSearch" class="page-size-select">
                <option :value="10">10</option>
                <option :value="20">20</option>
                <option :value="50">50</option>
              </select>
            </div>
          </div>

          <!-- Results List -->
          <div v-if="searchResults.courses.length > 0" class="results-list">
            <article
              v-for="course in searchResults.courses"
              :key="course.id"
              class="course-card"
              tabindex="0"
              role="button"
              @click="viewCourse(course.id)"
              @keydown.enter="viewCourse(course.id)"
              @keydown.space.prevent="viewCourse(course.id)"
            >
              <div class="course-card-header">
                <h3 class="course-card-title">{{ course.name }}</h3>
                <div class="course-card-meta">
                  <div class="course-card-competencies" v-if="(course.competencies || []).length">
                    <span v-for="comp in (course.competencies || [])" :key="comp.id" class="badge badge-competency">{{ comp.name }}</span>
                  </div>
                  <div class="course-card-highlights">
                    <div class="course-card-cost" :class="{ 'course-card-cost--free': course.costType === 'free' }">
                      <template v-if="course.costType === 'free'">Free</template>
                      <template v-else>${{ formatCost(course.cost) }}</template>
                    </div>
                    <span v-if="course.reviewCount > 0" class="badge badge-reviews">
                      {{ course.reviewCount }} Review{{ course.reviewCount !== 1 ? 's' : '' }}
                      <span v-if="course.averageOverallValue" class="rating-badge">
                        ⭐ {{ course.averageOverallValue.toFixed(1) }}
                      </span>
                    </span>
                  </div>
                </div>
              </div>
              <div class="course-card-body">
                <div class="course-card-details-grid">
                  <div class="course-detail-item">
                    <span class="course-detail-label">Provider</span>
                    <span class="course-detail-value">{{ course.providerName }}</span>
                  </div>
                  <div v-if="course.instructor" class="course-detail-item">
                    <span class="course-detail-label">Instructor</span>
                    <span class="course-detail-value">{{ course.instructor }}</span>
                  </div>
                  <div class="course-detail-item">
                    <span class="course-detail-label">Course Type</span>
                    <span class="course-detail-value">{{ formatCourseType(course.courseType) }}</span>
                  </div>
                  <div v-if="course.location" class="course-detail-item">
                    <span class="course-detail-label">Location</span>
                    <span class="course-detail-value">{{ course.location }}</span>
                  </div>
                  <div class="course-detail-item course-detail-item--credits">
                    <span class="course-detail-value course-detail-value--credits-num">{{ course.credits }}</span>
                    <span class="course-detail-label">CEU</span>
                  </div>
                </div>
                <div v-if="course.description" class="course-card-description">
                  {{ truncateDescription(course.description) }}
                </div>
              </div>
            </article>
          </div>

          <!-- No Results -->
          <div v-else class="no-results">
            <span class="no-results-icon" aria-hidden="true">
              <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round">
                <circle cx="11" cy="11" r="8"/>
                <path d="m21 21-4.35-4.35"/>
              </svg>
            </span>
            <p>No courses found matching your criteria.</p>
            <div class="no-results-actions">
              <button type="button" @click="clearFilters" class="btn btn-secondary">Clear Filters</button>
              <button type="button" @click="addNewCourse" class="btn btn-primary">Add New Course</button>
            </div>
          </div>

          <!-- Pagination -->
          <div v-if="searchResults.pagination.totalPages > 1" class="pagination">
            <button
              @click="goToPage(searchResults.pagination.currentPage - 1)"
              :disabled="!searchResults.pagination.hasPreviousPage"
              class="pagination-btn"
            >
              ← Previous
            </button>
            <div class="pagination-info">
              Page {{ searchResults.pagination.currentPage }} of {{ searchResults.pagination.totalPages }}
            </div>
            <button
              @click="goToPage(searchResults.pagination.currentPage + 1)"
              :disabled="!searchResults.pagination.hasNextPage"
              class="pagination-btn"
            >
              Next →
            </button>
          </div>
        </div>
      </main>
    </div>
  </div>
</template>

<script>
import { searchCourses, getFilterOptions } from '../../services/searchService.js'
import { formatCourseType as formatCourseTypeModel, getCourseTypes } from '../../models/Course.js'

const SEARCH_STATE_KEY = 'dietedics_search_state'

export default {
  name: 'SearchPage',
  data() {
    return {
      filters: {
        query: '',
        competencyId: '',
        providerType: '',
        courseType: '',
        costType: '',
        minCredits: null,
        maxCredits: null,
        maxCost: null,
        location: '',
        page: 1,
        pageSize: 10,
        sortBy: 'createdAt',
        sortOrder: 'desc',
        statusFilter: 'active'
      },
      searchResults: {
        courses: [],
        pagination: {
          currentPage: 1,
          pageSize: 10,
          totalResults: 0,
          totalPages: 0,
          hasNextPage: false,
          hasPreviousPage: false
        }
      },
      filterOptions: {
        competencies: [],
        providerTypes: [],
        courseTypes: [],
        creditsRange: { min: 0, max: 0 },
        maxCost: 0
      },
      loading: false,
      error: null,
      searchTimeout: null,
      saveTimeout: null
    }
  },
  computed: {
    courseTypeOptions() {
      return getCourseTypes()
    },
    hasActiveFilters() {
      return !!(
        this.filters.query ||
        this.filters.competencyId ||
        this.filters.providerType ||
        this.filters.courseType ||
        this.filters.costType ||
        this.filters.minCredits ||
        this.filters.maxCredits ||
        this.filters.maxCost ||
        this.filters.location
      )
    }
  },
  async mounted() {
    this.loadSearchState()
    await this.loadFilterOptions()
    await this.handleSearch()
  },
  beforeUnmount() {
    // Clean up timeouts
    if (this.searchTimeout) {
      clearTimeout(this.searchTimeout)
    }
    if (this.saveTimeout) {
      clearTimeout(this.saveTimeout)
    }
    // Save state when leaving the page
    this.saveSearchState()
  },
  watch: {
    // Watch for filter changes and save state (debounced)
    filters: {
      handler() {
        if (this.saveTimeout) {
          clearTimeout(this.saveTimeout)
        }
        this.saveTimeout = setTimeout(() => {
          this.saveSearchState()
        }, 1000) // Save 1 second after last change
      },
      deep: true
    }
  },
  methods: {
    loadSearchState() {
      try {
        const saved = localStorage.getItem(SEARCH_STATE_KEY)
        if (saved) {
          const savedFilters = JSON.parse(saved)
          // Merge saved filters with defaults, preserving structure
          this.filters = {
            query: savedFilters.query || '',
            competencyId: savedFilters.competencyId !== undefined && savedFilters.competencyId !== null ? String(savedFilters.competencyId) : '',
            providerType: savedFilters.providerType || '',
            courseType: savedFilters.courseType || '',
            costType: savedFilters.costType || '',
            minCredits: savedFilters.minCredits !== undefined && savedFilters.minCredits !== null ? Number(savedFilters.minCredits) : null,
            maxCredits: savedFilters.maxCredits !== undefined && savedFilters.maxCredits !== null ? Number(savedFilters.maxCredits) : null,

            maxCost: savedFilters.maxCost !== undefined && savedFilters.maxCost !== null ? Number(savedFilters.maxCost) : null,
            location: savedFilters.location || '',
            page: savedFilters.page || 1,
            pageSize: savedFilters.pageSize || 10,
            sortBy: savedFilters.sortBy || 'createdAt',
            sortOrder: savedFilters.sortOrder || 'desc',
            statusFilter: savedFilters.statusFilter || 'active'
          }
        }
      } catch (error) {
        console.error('Error loading search state:', error)
        // If there's an error, use default filters
      }
    },
    saveSearchState() {
      try {
        // Only save non-default values to keep storage clean
        const stateToSave = {
          query: this.filters.query || '',
          competencyId: this.filters.competencyId || '',
          providerType: this.filters.providerType || '',
          courseType: this.filters.courseType || '',
          costType: this.filters.costType || '',
          minCredits: this.filters.minCredits,
          maxCredits: this.filters.maxCredits,
          maxCost: this.filters.maxCost,
          location: this.filters.location || '',
          page: this.filters.page,
          pageSize: this.filters.pageSize,
          sortBy: this.filters.sortBy,
          sortOrder: this.filters.sortOrder,
          statusFilter: this.filters.statusFilter || 'active'
        }
        localStorage.setItem(SEARCH_STATE_KEY, JSON.stringify(stateToSave))
      } catch (error) {
        console.error('Error saving search state:', error)
      }
    },
    async loadFilterOptions() {
      try {
        this.filterOptions = await getFilterOptions()
      } catch (error) {
        console.error('Error loading filter options:', error)
      }
    },
    async handleSearch(resetPage = true) {
      this.loading = true
      this.error = null

      try {
        // Reset to page 1 if filters changed (unless explicitly changing page via goToPage)
        if (resetPage) {
          this.filters.page = 1
        }

        const results = await searchCourses(this.filters)
        this.searchResults = results
        // Save state after successful search
        this.saveSearchState()
      } catch (error) {
        this.error = 'Failed to search courses. Please try again.'
        console.error('Error searching courses:', error)
      } finally {
        this.loading = false
      }
    },
    toggleSortOrder() {
      this.filters.sortOrder = this.filters.sortOrder === 'asc' ? 'desc' : 'asc'
      this.handleSearch()
    },
    debouncedSearch() {
      if (this.searchTimeout) {
        clearTimeout(this.searchTimeout)
      }
      this.searchTimeout = setTimeout(() => {
        this.handleSearch()
      }, 500)
    },
    goToPage(page) {
      this.filters.page = page
      this.handleSearch(false) // Don't reset page when explicitly changing page
      // Scroll to top of results
      window.scrollTo({ top: 0, behavior: 'smooth' })
    },
    clearFilters() {
      this.filters = {
        query: '',
        competencyId: '',
        providerType: '',
        courseType: '',
        costType: '',
        minCredits: null,
        maxCredits: null,
        maxCost: null,
        location: '',
        page: 1,
        pageSize: this.filters.pageSize,
        sortBy: 'createdAt',
        sortOrder: 'desc',
        statusFilter: 'active'
      }
      // Clear saved state
      localStorage.removeItem(SEARCH_STATE_KEY)
      this.handleSearch()
    },
    viewCourse(courseId) {
      this.$router.push({ name: 'view-course', params: { id: courseId } })
    },
    addNewCourse() {
      this.$router.push({ name: 'add-course' })
    },
    formatCost(cost) {
      if (!cost) return '0.00'
      return parseFloat(cost).toFixed(2)
    },
    formatCourseType(type) {
      return formatCourseTypeModel(type)
    },
    truncateDescription(description) {
      if (!description) return ''
      return description.length > 150 ? description.substring(0, 150) + '...' : description
    }
  }
}
</script>

<style scoped>
/* Spacing scale: 8px base → 8, 16, 24, 32, 40, 48, 64 */
.search-page {
  max-width: 1400px;
  margin: 0 auto;
  padding: 48px 24px;
}

/* Hero: compact so it doesn't take too much space */
.hero {
  position: relative;
  text-align: center;
  margin-bottom: 32px;
  padding: 20px 24px;
  border-radius: 12px;
  overflow: hidden;
}

.hero-background {
  position: absolute;
  inset: 0;
  background: linear-gradient(
    135deg,
    var(--color-bg-main) 0%,
    var(--color-bg-dark) 50%,
    var(--color-primary-light) 120%
  );
  opacity: 0.15;
  pointer-events: none;
}

.hero-content {
  position: relative;
  z-index: 1;
}

.hero-title {
  font-size: 1.5rem;
  font-weight: 700;
  line-height: 1.35;
  color: var(--color-text-primary);
  margin: 0 0 8px 0;
}

.hero-subtitle {
  font-size: 1rem;
  color: var(--color-text-secondary);
  margin: 0;
}

.search-container {
  display: grid;
  grid-template-columns: 300px 1fr;
  gap: 32px;
}

/* Filters Sidebar: 1px border, neutral shadow */
.filters-sidebar {
  background: var(--color-surface-light);
  border: 1px solid var(--color-border-light);
  border-radius: 12px;
  padding: 24px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.06);
  height: fit-content;
  position: sticky;
  top: 24px;
}

.filters-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 24px;
  padding-bottom: 16px;
  border-bottom: 1px solid var(--color-border-light);
}

.filters-title {
  font-size: 1.25rem;
  font-weight: 600;
  color: var(--color-text-primary);
  margin: 0;
}

.clear-filters-btn {
  background: none;
  border: none;
  color: var(--color-primary-main);
  cursor: pointer;
  font-size: 0.9rem;
  font-weight: 600;
  padding: 6px 10px;
  border-radius: 6px;
  transition: background-color var(--transition-normal), color var(--transition-normal);
}

.clear-filters-btn:hover {
  background-color: var(--color-primary-light);
  color: var(--color-text-inverse);
}

.clear-filters-btn:focus-visible {
  outline: 2px solid var(--color-primary-main);
  outline-offset: 2px;
}

.filters-form {
  display: flex;
  flex-direction: column;
  gap: 24px;
}

.filter-group {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.filter-group label {
  font-weight: 600;
  color: var(--color-text-primary);
  font-size: 0.9375rem;
}

.filter-input {
  width: 100%;
  padding: 10px 12px;
  border: 1px solid var(--color-border-light);
  border-radius: 8px;
  font-size: 0.9375rem;
  font-family: inherit;
  background-color: var(--color-surface-light);
  color: var(--color-text-primary);
  transition: border-color var(--transition-normal);
}

.filter-input:focus {
  outline: none;
  border-color: var(--color-primary-main);
}

.filter-input:focus-visible {
  outline: 2px solid var(--color-primary-main);
  outline-offset: 0;
}

.range-inputs {
  display: flex;
  align-items: center;
  gap: 10px;
}

.range-input {
  flex: 1;
}

.range-separator {
  color: var(--color-text-secondary);
  font-size: 0.9rem;
}

/* Results Section */
.results-section {
  min-height: 400px;
}

.loading-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 16px;
  padding: 48px 24px;
  color: var(--color-text-secondary);
  font-size: 1rem;
}

.spinner {
  width: 40px;
  height: 40px;
  border: 3px solid var(--color-border-main);
  border-top-color: var(--color-primary-main);
  border-radius: 50%;
  animation: spin 0.8s linear infinite;
}

@keyframes spin {
  to {
    transform: rotate(360deg);
  }
}

.error-state {
  text-align: center;
  padding: 48px 24px;
  color: var(--color-text-secondary);
  font-size: 1rem;
}

.results-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 24px;
  padding-bottom: 16px;
  border-bottom: 1px solid var(--color-border-light);
  flex-wrap: wrap;
  gap: 16px;
}

.results-count {
  color: var(--color-text-primary);
  font-size: 1rem;
}

.results-sort {
  display: flex;
  align-items: center;
  gap: 10px;
  flex-wrap: wrap;
}

.results-sort label {
  color: var(--color-text-secondary);
  font-size: 0.9rem;
}

.results-header-select {
  padding: 8px 12px;
  border: 1px solid var(--color-border-light);
  border-radius: 8px;
  background-color: var(--color-surface-light);
  color: var(--color-text-primary);
  font-size: 0.9rem;
  font-family: inherit;
}

.results-header-select:focus-visible {
  outline: 2px solid var(--color-primary-main);
  outline-offset: 0;
}

.sort-direction-btn {
  padding: 8px 12px;
  border: 1px solid var(--color-border-light);
  border-radius: 8px;
  background-color: var(--color-surface-light);
  color: var(--color-text-primary);
  font-size: 1.1rem;
  cursor: pointer;
  line-height: 1;
  transition: border-color var(--transition-normal), background-color var(--transition-normal), color var(--transition-normal);
}

.sort-direction-btn:hover {
  border-color: var(--color-primary-main);
  background-color: var(--color-primary-light);
  color: var(--color-text-inverse);
}

.sort-direction-btn:focus-visible {
  outline: 2px solid var(--color-primary-main);
  outline-offset: 2px;
}

.results-per-page {
  display: flex;
  align-items: center;
  gap: 10px;
}

.results-per-page label {
  color: var(--color-text-secondary);
  font-size: 0.9rem;
}

.page-size-select {
  padding: 8px 12px;
  border: 1px solid var(--color-border-light);
  border-radius: 8px;
  background-color: var(--color-surface-light);
  color: var(--color-text-primary);
  font-size: 0.9rem;
  font-family: inherit;
}

.page-size-select:focus-visible {
  outline: 2px solid var(--color-primary-main);
  outline-offset: 0;
}

.results-list {
  display: flex;
  flex-direction: column;
  gap: 24px;
}

/* Course cards: 1px border, neutral shadow, accent strip at bottom */
.course-card {
  position: relative;
  background: var(--color-surface-light);
  border: 1px solid var(--color-border-light);
  border-radius: 12px;
  padding: 24px;
  cursor: pointer;
  transition: transform var(--transition-normal), box-shadow var(--transition-normal), border-color var(--transition-normal);
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.06);
  overflow: hidden;
}

.course-card::after {
  content: '';
  position: absolute;
  left: 0;
  right: 0;
  bottom: 0;
  height: 4px;
  background: linear-gradient(
    to right,
    transparent 0%,
    var(--color-accent-main) 25%,
    var(--color-accent-main) 75%,
    transparent 100%
  );
  border-radius: 0 0 11px 11px;
}

.course-card:hover {
  border-color: var(--color-primary-main);
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1), 0 2px 4px rgba(0, 0, 0, 0.04);
  transform: translateY(-2px);
}

.course-card:active {
  transform: translateY(0);
}

.course-card:focus-visible {
  outline: 2px solid var(--color-primary-main);
  outline-offset: 2px;
}

.course-card-header {
  display: flex;
  flex-direction: column;
  gap: 12px;
  margin-bottom: 16px;
}

.course-card-title {
  font-size: 1.25rem;
  font-weight: 600;
  color: var(--color-text-primary);
  margin: 0;
  line-height: 1.35;
}

.course-card-meta {
  display: flex;
  flex-wrap: wrap;
  justify-content: space-between;
  align-items: flex-start;
  gap: 12px;
}

.course-card-competencies {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  min-width: 0;
  flex: 1 1 auto;
}

.course-card-highlights {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  flex-shrink: 0;
  align-items: center;
  margin-left: auto;
  justify-content: flex-end;
}

.course-card-cost {
  padding: 6px 14px;
  border-radius: 8px;
  font-size: 1rem;
  font-weight: 700;
  border-left: 4px solid var(--color-accent-main);
  background: var(--color-accent-light);
  color: var(--color-accent-dark);
}

.course-card-cost--free {
  border-left-color: var(--color-success-main);
  background: var(--color-success-light);
  color: var(--color-success-dark);
}

.badge {
  padding: 4px 12px;
  border-radius: 20px;
  font-size: 0.8125rem;
  font-weight: 600;
  white-space: nowrap;
}

.badge-competency {
  background: var(--color-primary-light);
  color: var(--color-text-inverse);
}

.badge-credits {
  background: var(--color-secondary-light);
  color: var(--color-text-primary);
}

.badge-reviews {
  background: var(--color-primary-light);
  color: var(--color-text-primary);
  display: flex;
  align-items: center;
  gap: 6px;
}

.rating-badge {
  font-weight: 600;
  color: var(--color-primary-dark);
}

.course-card-body {
  display: flex;
  flex-direction: column;
  gap: 16px;
}

/* Course card details: same label/value pattern as ViewCourse Details section */
.course-card-details-grid {
  display: flex;
  flex-wrap: wrap;
  gap: 10px 16px;
}

.course-detail-item {
  display: flex;
  flex-direction: column;
  gap: 4px;
  padding: 10px 12px;
  min-width: 160px;
  background: var(--color-bg-dark);
  border-radius: 8px;
  border: 1px solid var(--color-border-light);
}

.course-detail-item--credits {
  margin-left: auto;
  flex-direction: row;
  align-items: baseline;
  gap: 6px;
  background: linear-gradient(135deg, var(--color-bg-dark) 0%, var(--color-secondary-light) 18%);
  border-color: var(--color-secondary-accent);
}

.course-detail-item--credits .course-detail-value--credits-num {
  color: var(--color-primary-dark);
  font-weight: 700;
  font-size: 1.375rem;
}

.course-detail-label {
  font-size: 0.6875rem;
  font-weight: 500;
  color: var(--color-text-light);
  text-transform: uppercase;
  letter-spacing: 0.04em;
}

.course-detail-value {
  font-size: 0.9375rem;
  font-weight: 600;
  color: var(--color-text-primary);
  line-height: 1.35;
}

.free-badge {
  background: var(--color-success-light);
  color: var(--color-success-dark);
  padding: 2px 8px;
  border-radius: 4px;
  font-size: 0.8125rem;
  font-weight: 600;
}

.course-card-description {
  color: var(--color-text-secondary);
  font-size: 0.9375rem;
  line-height: 1.6;
  margin-top: 8px;
}

/* No results: icon + actions */
.no-results {
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
  padding: 48px 24px;
  color: var(--color-text-secondary);
  font-size: 1rem;
}

.no-results-icon {
  display: flex;
  width: 48px;
  height: 48px;
  margin-bottom: 16px;
  color: var(--color-border-dark);
}

.no-results-icon svg {
  width: 100%;
  height: 100%;
}

.no-results-actions {
  display: flex;
  gap: 16px;
  justify-content: center;
  margin-top: 24px;
  flex-wrap: wrap;
}

.btn {
  padding: 12px 24px;
  border-radius: 8px;
  cursor: pointer;
  font-weight: 600;
  font-size: 1rem;
  font-family: inherit;
  transition: transform var(--transition-normal), box-shadow var(--transition-normal), background-color var(--transition-normal), border-color var(--transition-normal);
}

.btn-primary {
  background-color: var(--color-primary-main);
  color: var(--color-text-inverse);
  border: 1px solid var(--color-primary-dark);
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.08);
}

.btn-primary:hover {
  background-color: var(--color-primary-dark);
  border-color: var(--color-primary-dark);
  transform: translateY(-2px);
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.12);
}

.btn-primary:active {
  transform: translateY(0);
}

.btn-primary:focus-visible {
  outline: 2px solid var(--color-primary-dark);
  outline-offset: 2px;
}

.btn-secondary {
  background-color: var(--color-surface-light);
  color: var(--color-text-primary);
  border: 1px solid var(--color-border-main);
}

.btn-secondary:hover {
  background-color: var(--color-bg-dark);
  border-color: var(--color-border-dark);
}

.btn-secondary:focus-visible {
  outline: 2px solid var(--color-primary-main);
  outline-offset: 2px;
}

.pagination {
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 24px;
  margin-top: 48px;
  padding-top: 32px;
  border-top: 1px solid var(--color-border-light);
}

.pagination-btn {
  padding: 12px 24px;
  background-color: var(--color-primary-main);
  color: var(--color-text-inverse);
  border: 1px solid var(--color-primary-dark);
  border-radius: 8px;
  cursor: pointer;
  font-weight: 600;
  font-size: 1rem;
  font-family: inherit;
  transition: transform var(--transition-normal), box-shadow var(--transition-normal), background-color var(--transition-normal);
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.08);
}

.pagination-btn:hover:not(:disabled) {
  background-color: var(--color-primary-dark);
  transform: translateY(-2px);
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
}

.pagination-btn:active:not(:disabled) {
  transform: translateY(0);
}

.pagination-btn:focus-visible {
  outline: 2px solid var(--color-primary-dark);
  outline-offset: 2px;
}

.pagination-btn:disabled {
  background-color: var(--color-border-main);
  border-color: var(--color-border-main);
  cursor: not-allowed;
  opacity: 0.6;
}

.pagination-info {
  color: var(--color-text-secondary);
  font-weight: 500;
  font-size: 0.9375rem;
}

@media (max-width: 968px) {
  .search-container {
    grid-template-columns: 1fr;
  }

  .filters-sidebar {
    position: static;
  }
}

@media (max-width: 768px) {
  .search-page {
    padding: 32px 16px;
  }

  .hero {
    padding: 16px;
    margin-bottom: 24px;
  }

  .hero-title {
    font-size: 1.25rem;
  }

  .hero-subtitle {
    font-size: 0.9375rem;
  }

  .course-card-details-grid .course-detail-item {
    min-width: 100%;
  }

  .results-header {
    flex-direction: column;
    align-items: flex-start;
    gap: 16px;
  }
}
</style>

